import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, LoginCredentials } from '@/shared/types';
import { sessionService } from '@/shared/services/session.service';

// Tipos de acciones para las sagas
export interface LoginRequest {
  credentials: LoginCredentials;
}

export interface RegisterRequest {
  userData?: any; // Tipo específico de registro con todos los datos
  credentials?: {
    email: string;
    password: string;
  };
}

export interface OnboardingRequest {
  onboardingData: {
    firstName: string;
    lastName: string;
    phone?: string;
    company?: string;
    industry?: string;
    propertyTypes?: string[];
    budget?: string;
    experience?: string;
    role?: string;
    /** GH#2107 — datos fiscales del paso 2 del alta; solo el dueño, solo en su primer onboarding. */
    fiscal?: import('@/features/onboarding/utils/fiscal-alta').FiscalOnboarding;
  };
}

export interface AutoCompleteOnboardingRequest {
  // No necesita datos adicionales, usa los datos existentes del usuario
}

export interface OAuthCallbackRequest {
  token: string;
  refreshToken: string;
  userId: string;
}

export interface RefreshTokenRequest {
  refreshToken?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  code: string;
  newPassword: string;
}

// Estado inicial
export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  forgotPasswordSent: boolean; // Indica que el código de recuperación fue enviado exitosamente
  resetPasswordSuccess: boolean; // Indica que la contraseña fue restablecida exitosamente
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  loading: false, // Cambiado a false para evitar loader infinito
  error: null,
  forgotPasswordSent: false,
  resetPasswordSuccess: false,
};

/**
 * GH#2716: un 400 del validador del API trae `message` como LISTA (medido en producción) y las sagas lo pasan tal cual;
 * `error` es texto y una lista se pintaba pegada, sin separador (`<p>{error}</p>`).
 */
const comoTexto = (mensaje: unknown): string => (Array.isArray(mensaje) ? mensaje.join(' ') : String(mensaje));

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Acciones que disparan sagas
    loginRequest: (state, _action: PayloadAction<LoginRequest>) => {
      state.loading = true;
      state.error = null;
    },
    registerRequest: (state, _action: PayloadAction<RegisterRequest>) => {
      state.loading = true;
      state.error = null;
    },
    onboardingRequest: (state, _action: PayloadAction<OnboardingRequest>) => {
      state.loading = true;
      state.error = null;
    },
    autoCompleteOnboardingRequest: (state, _action: PayloadAction<AutoCompleteOnboardingRequest>) => {
      state.loading = true;
      state.error = null;
    },
    logoutRequest: state => {
      state.loading = true;
      state.error = null;
    },
    refreshTokenRequest: (
      state,
      _action: PayloadAction<RefreshTokenRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    verifyTokenRequest: state => {
      state.loading = true;
      state.error = null;
    },
    oauthCallbackRequest: (
      state,
      _action: PayloadAction<OAuthCallbackRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    forgotPasswordRequest: (state, _action: PayloadAction<ForgotPasswordRequest>) => {
      state.loading = true;
      state.error = null;
      state.forgotPasswordSent = false; // Resetear al iniciar nueva solicitud
    },
    resetPasswordRequest: (state, _action: PayloadAction<ResetPasswordRequest>) => {
      state.loading = true;
      state.error = null;
      state.resetPasswordSuccess = false; // Resetear al iniciar nueva solicitud
    },

    // Acciones que actualizan el estado (llamadas desde sagas)
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        token: string | null;
        refreshToken: string | null;
      }>
    ) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.error = null;

      // #1208: espejo de adminLoginSuccess (admin-slice.ts), que ya limpia la
      // sesión regular al entrar como admin. Faltaba la dirección inversa: sin
      // esto, una sesión admin vieja en el navegador (ej. Gabriel probando como
      // SUPER_ADMIN) sobrevive al login normal — AuthGuard prioriza admin sobre
      // user (combinedUser) y getToken() prioriza el token admin, así que un
      // login real y correcto queda evaluado con el rol/token de OTRO usuario.
      sessionService.clearSession(true);
    },
    registerSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        token: string | null;
        refreshToken: string | null;
      }>
    ) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.error = null;

      // #1208: misma limpieza que loginSuccess — un registro también establece
      // sesión regular y no debe heredar un admin viejo del navegador.
      sessionService.clearSession(true);
    },
    onboardingSuccess: (state, action: PayloadAction<{ user: User }>) => {
      state.loading = false;
      state.user = action.payload.user;
      state.error = null;
    },
    autoCompleteOnboardingSuccess: (state, action: PayloadAction<{ user: User; wasAutoCompleted: boolean }>) => {
      state.loading = false;
      state.user = action.payload.user;
      state.error = null;
    },
    logoutSuccess: state => {
      state.loading = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    refreshTokenSuccess: (
      state,
      action: PayloadAction<{
        token: string | null;
        refreshToken: string | null;
      }>
    ) => {
      state.loading = false;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.error = null;
    },
    verifyTokenSuccess: (state, action: PayloadAction<User>) => {
      state.loading = false;
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    oauthCallbackSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        token: string | null;
        refreshToken: string | null;
      }>
    ) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.error = null;

      // #1208: idem loginSuccess — el callback de OAuth también establece
      // sesión regular.
      sessionService.clearSession(true);
    },
    forgotPasswordSuccess: state => {
      state.loading = false;
      state.error = null;
      state.forgotPasswordSent = true; // Indica que el código fue enviado
    },
    resetPasswordSuccess: state => {
      state.loading = false;
      state.error = null;
      state.resetPasswordSuccess = true; // Indica que la contraseña fue restablecida
    },
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;

      sessionService.saveSession({
        user: action.payload,
      });
    },
    authFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = comoTexto(action.payload);
      state.forgotPasswordSent = false; // Resetear en caso de error
      state.resetPasswordSuccess = false; // Resetear en caso de error
    },
    // GH#2716: el reseteo falla SIN apagar `forgotPasswordSent`. `authFailure` lo apaga (sirve cuando falla el pedido
    // del código), y la vista «Nueva contraseña» exige que esté encendido: con cualquier error del API la persona caía
    // en la vista de correo y el aviso quedaba debajo del campo de correo. Aquí se queda donde está y corrige.
    resetPasswordFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = comoTexto(action.payload);
      state.resetPasswordSuccess = false;
    },
    clearError: state => {
      state.error = null;
    },
    clearPasswordResetState: state => {
      state.forgotPasswordSent = false;
      state.resetPasswordSuccess = false;
      state.error = null;
    },
    clearAuth: state => {
      // Limpiar sesión usando el servicio centralizado (isAdmin=false)
      sessionService.clearSession(false);

      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
    initializeAuth: state => {
      try {
        const sessionData = sessionService.restoreSession(false); // ← isAdmin = false

        if (sessionData) {
          state.user = sessionData.user;
          state.token = sessionData.token || null;
          state.refreshToken = sessionData.refreshToken || null;
          state.isAuthenticated = true;
          state.loading = false;
          state.error = null;

          // Con cookies HTTP-only, no necesitamos actualizar tokens manualmente
        } else {
          state.loading = false;
        }
      } catch (error) {
        sessionService.clearSession(false); // ← isAdmin = false
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
      }
    },
  },
});

export const {
  loginRequest,
  registerRequest,
  onboardingRequest,
  autoCompleteOnboardingRequest,
  logoutRequest,
  refreshTokenRequest,
  verifyTokenRequest,
  oauthCallbackRequest,
  forgotPasswordRequest,
  resetPasswordRequest,
  loginSuccess,
  registerSuccess,
  onboardingSuccess,
  autoCompleteOnboardingSuccess,
  logoutSuccess,
  refreshTokenSuccess,
  verifyTokenSuccess,
  oauthCallbackSuccess,
  forgotPasswordSuccess,
  resetPasswordSuccess,
  updateUser,
  authFailure,
  resetPasswordFailure,
  clearError,
  clearPasswordResetState,
  clearAuth,
  initializeAuth,
} = authSlice.actions;

export default authSlice.reducer;
